// The five-box balance sheet of *Key Management Ratios* (Ciaran Walsh, figure 2.4):
// assets on the left as two boxes, liabilities and funds on the right as three. The
// two columns total to the same figure, and the four terms below — total assets,
// capital employed, net worth, working capital — are read straight off the boxes.
//
// The boxes are sized in proportion to their value, which is what makes the layout
// worth drawing rather than tabulating: the shape of the balance sheet is the point.

import type { BalanceSheet as Sheet, BalanceSheetTerms } from "./ledger";
import { formatMoney } from "./ledger";

/** A box thinner than this cannot hold its label, so it is floored. */
const MIN_HEIGHT_PCT = 14;

export function BalanceSheetView({
  sheet,
  terms,
  currency,
}: {
  sheet: Sheet;
  terms: BalanceSheetTerms;
  currency: string;
}) {
  const total = terms.totalAssets;

  return (
    <div className="sheet">
      <div className="sheet-grid">
        <div className="sheet-col">
          <span className="sheet-col-head">Assets</span>
          <Box
            code="FA"
            name="Fixed assets"
            amount={sheet.fixedAssets}
            total={total}
            currency={currency}
          />
          <Box
            code="CA"
            name="Current assets"
            amount={sheet.currentAssets}
            total={total}
            currency={currency}
          />
          <span className="sheet-total">{formatMoney(total, currency)}</span>
        </div>

        <div className="sheet-col">
          <span className="sheet-col-head">Liabilities / Funds</span>
          <Box
            code="OF"
            name="Owners' funds"
            amount={sheet.ownersFunds}
            total={total}
            currency={currency}
          />
          <Box
            code="LTL"
            name="Long-term loans"
            amount={sheet.longTermLoans}
            total={total}
            currency={currency}
          />
          <Box
            code="CL"
            name="Current liabilities"
            amount={sheet.currentLiabilities}
            total={total}
            currency={currency}
          />
          <span className="sheet-total">
            {formatMoney(sheet.ownersFunds + sheet.longTermLoans + sheet.currentLiabilities, currency)}
          </span>
        </div>
      </div>

      {/* The four terms, each with the formula it is read from — the book teaches the
       * relationship, not the number, and the formula is what makes it stick. */}
      <ul className="sheet-terms">
        <Term
          code="TA"
          name="Total assets"
          formula="FA + CA"
          amount={terms.totalAssets}
          currency={currency}
        />
        <Term
          code="CE"
          name="Capital employed"
          formula="TA − CL"
          amount={terms.capitalEmployed}
          currency={currency}
        />
        <Term
          code="NW"
          name="Net worth"
          formula="TA − CL − LTL"
          amount={terms.netWorth}
          currency={currency}
        />
        <Term
          code="WC"
          name="Working capital"
          formula="CA − CL"
          amount={terms.workingCapital}
          currency={currency}
        />
      </ul>
    </div>
  );
}

/** One box, its height proportional to its share of the balance sheet. */
function Box({
  code,
  name,
  amount,
  total,
  currency,
}: {
  code: string;
  name: string;
  amount: number;
  total: number;
  currency: string;
}) {
  const share = total === 0 ? 0 : (amount / total) * 100;
  const height = Math.max(share, MIN_HEIGHT_PCT);

  return (
    <div className="sheet-box" style={{ flexGrow: height }} data-empty={amount === 0}>
      <span className="sheet-box-head">
        <span className="sheet-box-code">{code}</span>
        <span className="sheet-box-name">{name}</span>
      </span>
      <span className="sheet-box-amount">{formatMoney(amount, currency)}</span>
    </div>
  );
}

function Term({
  code,
  name,
  formula,
  amount,
  currency,
}: {
  code: string;
  name: string;
  formula: string;
  amount: number;
  currency: string;
}) {
  return (
    <li className="sheet-term">
      <span className="sheet-term-code">{code}</span>
      <span className="sheet-term-name">{name}</span>
      <span className="sheet-term-formula">{formula}</span>
      <span className="sheet-term-amount">{formatMoney(amount, currency)}</span>
    </li>
  );
}
